import React, {
  useMemo,
  useReducer,
  useState,
  useEffect,
  useCallback,
  useRef
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { showToast } from "../../ui/toast";
import {
  FiEdit2,
  FiUserPlus,
  FiPlusCircle,
  FiTrash,
  FiMoreHorizontal,
  FiShare,
  FiLogIn,
  FiAlertTriangle,
  FiCheck,
  FiClock,
  FiX,
  FiSearch,
  FiPaperclip,
  FiLink,
  FiChevronLeft,
  FiChevronDown
} from "react-icons/fi";
import Header from "../../components/Header/Header";
import ProjectCardLoader from "../../components/ProjectCardLoader";
import { uploadFile } from "../../services/firebaseStorageService";
import Cookies from "js-cookie";
import useBugs from "../../hooks/useBugs";
import BugModal from "./modals/BugModal";
import VersionModal from "./modals/VersionModal";
import UserModal from "./modals/UserModal";
import InfoModal from "./modals/InfoModal";
import DeleteModal from "./modals/DeleteModal";
import ShareModal from "./modals/ShareModal";
import RenameVersionModal from "./modals/RenameVersionModal";
import DeadlineModal from "./modals/DeadlineModal";
import "./Bugs.css";
import { shareProjectInvite } from "../../services/ShareService";
import { updateBug, getBugsByProject, getBugDetails } from "../../services/BugService";
import EditProjectModal from "./modals/EditProjectModal";
import Modal from "../../components/Modal/Modal";
import BugDetail from "../BugDetail/BugDetail";
import { createInvite } from "../../services/InviteService";
import { getAllProjects } from "../../services/ProjectService";

const VERSION_META_PREFIX = "buglog:versionMeta:";
const GLOBAL_VERSION_INDEX_KEY = "buglog:globalVersionIndex";

const VERSION_UID_PREFIX = "BL-";
const VERSION_ID_MIN_LEN = 6;

const safeParse = (v) => {
  try {
    if (!v) return null;
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const safeStringify = (v) => {
  try {
    return JSON.stringify(v);
  } catch {
    return "";
  }
};

const toBool = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "yes";

const normalizeId = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const getProjectDisplayName = (project = {}) => {
  try {
    const key = project?.uniq || project?.id;
    const stored = key ? localStorage.getItem(`title:${key}`) || "" : "";
    if (stored && String(stored).trim()) return String(stored).trim();
  } catch {
  }

  return String(project?.name || project?.project_name || project?.title || "").trim();
};

const canManageProjectAccess = (project, currentUserId) => {
  const creatorId = normalizeId(
    project?.created_by ??
      project?.created_by_id ??
      project?.owner_id ??
      project?.user_id
  );

  const rawRole = String(
    project?.role ??
      project?.member_role ??
      project?.project_role ??
      project?.permission ??
      ""
  )
    .trim()
    .toLowerCase();

  if (
    toBool(project?.is_owner) ||
    toBool(project?.owner) ||
    toBool(project?.is_admin) ||
    toBool(project?.admin) ||
    toBool(project?.is_manager) ||
    toBool(project?.manager)
  ) {
    return true;
  }

  if (["owner", "admin", "administrator", "manager", "yönetici"].includes(rawRole)) {
    return true;
  }

  return !!(currentUserId && creatorId && currentUserId === creatorId);
};

const getUidDigits = (value) => {
  const t = String(value || "").trim().toUpperCase();
  if (!t) return "";
  if (t.startsWith(VERSION_UID_PREFIX)) return t.slice(VERSION_UID_PREFIX.length).replace(/\D/g, "");
  return t.replace(/\D/g, "");
};

const normalizeVersionUid = (value) => {
  const digits = getUidDigits(value);
  if (!digits) return "";
  return `${VERSION_UID_PREFIX}${digits}`;
};

const isVersionUid = (value) => {
  const uid = normalizeVersionUid(value);
  if (!uid) return false;
  const m = uid.match(/^BL-(\d+)$/);
  if (!m) return false;
  return m[1].length >= VERSION_ID_MIN_LEN;
};

const isLegacyPrefixedId = (value) => {
  const s = String(value || "").trim().toUpperCase();
  return /^V-\d+$/.test(s) || /^V\d+$/.test(s);
};

const readGlobalVersionIndex = () => {
  try {
    const raw = localStorage.getItem(GLOBAL_VERSION_INDEX_KEY);
    const parsed = safeParse(raw);

    const base =
      parsed && typeof parsed === "object"
        ? parsed
        : { byVersionKey: {}, byId: {} };

    const byVersionKey =
      base.byVersionKey && typeof base.byVersionKey === "object" ? base.byVersionKey : {};
    const byId = base.byId && typeof base.byId === "object" ? base.byId : {};

    return { byVersionKey, byId };
  } catch {
    return { byVersionKey: {}, byId: {} };
  }
};

const writeGlobalVersionIndex = (idx) => {
  try {
    localStorage.setItem(GLOBAL_VERSION_INDEX_KEY, safeStringify(idx || {}));
  } catch {
  }
};

const genVersionUid = (byId) => {
  const used = byId && typeof byId === "object" ? byId : {};
  for (let i = 0; i < 40; i++) {
    let n = 0;
    try {
      if (typeof crypto !== "undefined" && crypto.getRandomValues) {
        const arr = new Uint32Array(2);
        crypto.getRandomValues(arr);
        const a = arr[0] >>> 0;
        const b = arr[1] >>> 0;
        n = (a * 1000000000 + (b % 1000000000)) % 9000000000;
      } else {
        n = Math.floor(Math.random() * 9000000000);
      }
    } catch {
      n = Math.floor(Math.random() * 9000000000);
    }

    const digits = String(1000000000 + (n % 9000000000));
    const uid = `${VERSION_UID_PREFIX}${digits}`;
    if (!used[uid]) return uid;
  }

  const fallbackDigits = String(Date.now());
  const fallbackUid = `${VERSION_UID_PREFIX}${fallbackDigits}`;
  if (!used?.[fallbackUid]) return fallbackUid;
  return `${fallbackUid}${Math.floor(Math.random() * 1000)}`;
};

const buildGlobalVersionKey = (projectUniq, version) => {
  const p = String(projectUniq || "").trim();
  const vid = version?.id != null ? String(version.id).trim() : "";
  const vname = version?.version_name != null ? String(version.version_name).trim() : "";
  if (!p) return "";
  if (vid) return `p:${p}|vid:${vid}`;
  if (vname) return `p:${p}|vname:${vname}`;
  return "";
};

const readVersionMetaMap = (projectId) => {
  try {
    if (!projectId) return {};
    const raw = localStorage.getItem(`${VERSION_META_PREFIX}${projectId}`);
    const parsed = safeParse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
};

const writeVersionMetaMap = (projectId, map) => {
  try {
    if (!projectId) return;
    localStorage.setItem(`${VERSION_META_PREFIX}${projectId}`, safeStringify(map || {}));
  } catch {
  }
};

const resolveMetaKey = (version) => {
  const id = version?.id != null ? String(version.id) : "";
  const name = version?.version_name ? String(version.version_name) : "";
  if (id) return `id:${id}`;
  if (name) return `name:${name}`;
  return "";
};

const getVersionMeta = (projectId, version) => {
  const map = readVersionMetaMap(projectId);
  const k = resolveMetaKey(version);
  if (!k) return null;
  const v = map[k];
  if (!v || typeof v !== "object") return null;
  return v;
};

const patchVersionMeta = (projectId, version, patch) => {
  const map = readVersionMetaMap(projectId);
  const k = resolveMetaKey(version);
  if (!k) return;

  const prev = map[k] && typeof map[k] === "object" ? map[k] : {};
  const next = { ...prev, ...(patch || {}) };

  if (typeof next.deadline === "undefined") next.deadline = prev.deadline || "";
  if (typeof next.closed_at === "undefined") next.closed_at = prev.closed_at || "";

  map[k] = next;
  writeVersionMetaMap(projectId, map);

  try {
    window.dispatchEvent(new CustomEvent("version:changed", { detail: { projectId } }));
  } catch {
  }
};

const applyMetaToVersion = (projectId, version) => {
  if (!version) return version;
  const meta = getVersionMeta(projectId, version);
  if (!meta) return version;

  const out = { ...version };

  if (typeof meta.deadline === "string") out.deadline = meta.deadline;
  if (typeof meta.closed_at === "string") out.closed_at = meta.closed_at;

  return out;
};

const decodeHtmlEntities = (value = "") => {
  const raw = String(value || "");
  if (!raw) return "";

  try {
    if (typeof document !== "undefined") {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = raw;
      return textarea.value || textarea.textContent || "";
    }
  } catch {
  }

  return raw
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'");
};

const htmlToText = (s = "") => {
  if (!s) return "";
  try {
    let str = String(s);
    str = str.replace(/<li[^>]*>/gi, "• ");
    str = str.replace(/<br\s*\/?>/gi, "\n");
    str = str.replace(/<\/(p|div|li|ul|ol|h[1-6])>/gi, "\n");
    str = str.replace(/<[^>]*>/g, "");
    str = decodeHtmlEntities(str);
    str = str.replace(/\u00a0/g, " ");
    str = str
      .split("\n")
      .map((line) => line.replace(/\s+/g, " ").trim())
      .filter((line, i, a) => !(line === "" && a[i - 1] === ""))
      .join("\n")
      .trim();
    return str;
  } catch {
    return String(s)
      .replace(/<li[^>]*>/gi, "• ")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|ul|ol|h[1-6])>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&#160;/gi, " ")
      .replace(/\u00a0/g, " ")
      .split("\n")
      .map((line) => line.replace(/\s+/g, " ").trim())
      .filter((line, i, a) => !(line === "" && a[i - 1] === ""))
      .join("\n")
      .trim();
  }
};

const getFileName = (u = "") => {
  try {
    const url = new URL(u);
    return decodeURIComponent(url.pathname.split("/").pop() || "dosya");
  } catch {
    const clean = (u || "").split("?")[0];
    return decodeURIComponent(clean.split("/").pop() || "dosya");
  }
};

const getHost = (u = "") => {
  try {
    const url = new URL(u);
    return url.host;
  } catch {
    return (u || "").replace(/^https?:\/\//, "").split("/")[0];
  }
};

const isFirebaseStorageUrl = (u = "") => {
  try {
    const url = new URL(u);
    return url.host === "firebasestorage.googleapis.com";
  } catch {
    return false;
  }
};

const getFirebaseObjectPath = (u = "") => {
  try {
    const url = new URL(u);
    if (url.host !== "firebasestorage.googleapis.com") return "";
    const p = url.pathname || "";
    const idx = p.indexOf("/o/");
    if (idx === -1) return "";
    const encoded = p.slice(idx + 3);
    if (!encoded) return "";
    const decoded = decodeURIComponent(encoded);
    return decoded || "";
  } catch {
    return "";
  }
};

const getDisplaySource = (u = "") => {
  const objPath = getFirebaseObjectPath(u);
  if (objPath) return objPath;
  return getHost(u);
};

const getCover = (uniq) => {
  try {
    return uniq ? localStorage.getItem(`cover:${uniq}`) || "" : "";
  } catch {
    return "";
  }
};

const getColor = (uniq) => {
  try {
    return uniq ? localStorage.getItem(`color:${uniq}`) || "" : "";
  } catch {
    return "";
  }
};

const isImageUrl = (u = "") => /\.?(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(u || "");
const isPdfUrl = (u = "") => /\.?pdf(\?.*)?$/i.test(u || "");

const modalInit = {
  bug: false,
  version: false,
  user: false,
  info: false,
  deleteConfirm: false,
  share: false
};
const reducer = (s, a) => ({ ...s, [a.type]: a.open });

const initialForm = {
  title: "",
  description: "",
  file: null,
  fileUrl: "",
  uploadStatus: "",
  version: "",
  email: "",
  confirmName: "",
  preview: "",
  nowVersion: "",
  externalUrl: ""
};

const readMyUserId = () => {
  try {
    const uid =
      localStorage.getItem("user_id") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("uid") ||
      Cookies.get("user_id") ||
      Cookies.get("uid") ||
      null;
    return uid != null ? String(uid) : "";
  } catch {
    return "";
  }
};

const normalizeMemberEmail = (value) => String(value || "").trim().toLowerCase();

const isCurrentProjectUser = (user, currentUserId, currentUserEmail) => {
  const memberId = String(user?.id ?? user?.user_id ?? "").trim();
  const memberEmail = normalizeMemberEmail(user?.email);

  if (currentUserId && memberId && memberId === String(currentUserId).trim()) {
    return true;
  }

  if (currentUserEmail && memberEmail && memberEmail === normalizeMemberEmail(currentUserEmail)) {
    return true;
  }

  return false;
};

const getProjectUserRoleLabel = (role) => {
  if (role === "owner") return "Sahip";
  if (role === "manager") return "Yönetici";
  return "Üye";
};

const withBugParam = (path, bugId) => {
  try {
    const base = String(path || "").trim() || "/";
    const cleanBase = base.split("#")[0];
    const parts = cleanBase.split("?");
    const p = parts[0] || "/";
    const qs = new URLSearchParams(parts[1] || "");
    if (bugId != null && String(bugId).trim()) {
      qs.set("bug", String(bugId).trim());
    }
    const q = qs.toString();
    return q ? `${p}?${q}` : p;
  } catch {
    return String(path || "/");
  }
};

const notifyProjectUsers = async (payload) => {
  try {
    const res = await fetch("/notifications.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload || {})
    });
    return await res.json();
  } catch {
    return null;
  }
};

const Bugs = () => {
  const { projectId, isOwner, projectName } = useParams();
  const navigate = useNavigate();
  const {
    bugs,
    versions,
    users,
    loading,
    createBug,
    toggleBug,
    removeBug,
    createVersion,
    renameVersion,
    addUser,
    removeUser,
    changeUserRole,
    leave,
    removeProject,
    currentUserId,
    currentUserEmail,
    canManageMembers
  } = useBugs(projectId);

  const [modal, dispatch] = useReducer(reducer, modalInit);
  const [form, setForm] = useState(initialForm);
  const [isEdit, setIsEdit] = useState(false);
  const [editingBugId, setEditingBugId] = useState(null);

  const [shadowBugs, setShadowBugs] = useState(null);

  const [renameDlg, setRenameDlg] = useState({
    open: false,
    id: null,
    name: ""
  });
  const [deadlineDlg, setDeadlineDlg] = useState({
    open: false,
    id: null,
    date: ""
  });
  const [search, setSearch] = useState("");
  const token = Cookies.get("token");
  const canManageProjectUsers = canManageMembers || isOwner === "1";

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);

  const [detailId, setDetailId] = useState(null);

  const [deleteBugDlg, setDeleteBugDlg] = useState({
    open: false,
    id: null,
    title: ""
  });

  const [attViewer, setAttViewer] = useState({
    open: false,
    url: "",
    title: "",
    kind: "file"
  });
  const [projectPanelOpen, setProjectPanelOpen] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState(false);
  const [projectSort, setProjectSort] = useState("az");

  const initials = useMemo(
    () =>
      projectName
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase(),
    [projectName]
  );

  useEffect(() => {
    let alive = true;
    setProjectOptionsLoading(true);

    getAllProjects()
      .then((list) => {
        if (!alive) return;
        setProjectOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!alive) return;
        setProjectOptions([]);
      })
      .finally(() => {
        if (alive) {
          setProjectOptionsLoading(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [projectId]);

  const sortedProjectOptions = useMemo(() => {
    const list = [...projectOptions].filter((project) => String(project?.uniq || project?.id || "").trim());

    list.sort((a, b) => {
      const an = getProjectDisplayName(a);
      const bn = getProjectDisplayName(b);
      const result = an.localeCompare(bn, "tr", { sensitivity: "base" });
      return projectSort === "za" ? result * -1 : result;
    });

    return list;
  }, [projectOptions, projectSort]);

  const handleProjectSwitch = useCallback(
    (project) => {
      const targetProjectId = String(project?.uniq || project?.id || "").trim();
      if (!targetProjectId) return;

      const nextProjectName = getProjectDisplayName(project) || "Proje";
      const nextCanManage = canManageProjectAccess(project, currentUserId) ? 1 : 0;

      dispatch({ type: "info", open: false });
      navigate(`/bugs/${targetProjectId}/${nextCanManage}/${encodeURIComponent(nextProjectName)}`);
    },
    [navigate, currentUserId]
  );

  const [coverTick, setCoverTick] = useState(0);
  const [versionTick, setVersionTick] = useState(0);
  const [versionUidTick, setVersionUidTick] = useState(0);

  useEffect(() => {
    const onChanged = (e) => {
      if (!e?.detail?.projectId || e.detail.projectId === projectId) {
        setCoverTick((x) => x + 1);
        setVersionTick((x) => x + 1);
        setEditModalOpen(false);
      }
    };

    const onStorage = (e) => {
      const coverKey = `cover:${projectId}`;
      const colorKey = `color:${projectId}`;
      const versionKey = `${VERSION_META_PREFIX}${projectId}`;

      if (e.key === coverKey || e.key === colorKey) {
        setCoverTick((x) => x + 1);
        setEditModalOpen(false);
      }

      if (e.key === versionKey) {
        setVersionTick((x) => x + 1);
      }

      if (e.key === GLOBAL_VERSION_INDEX_KEY) {
        setVersionUidTick((x) => x + 1);
      }
    };

    window.addEventListener("cover:changed", onChanged);
    window.addEventListener("version:changed", onChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("cover:changed", onChanged);
      window.removeEventListener("version:changed", onChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [projectId]);

  const cover = useMemo(() => getCover(projectId), [projectId, coverTick]);
  const color = useMemo(() => getColor(projectId), [projectId, coverTick]);

  useEffect(() => {
    const root = document.documentElement;
    const fallback = "#0169ff";
    const valid = typeof color === "string" && color.trim().length > 0 ? color.trim() : null;
    root.style.setProperty("--accent", valid || fallback);
    return () => {
      root.style.setProperty("--accent", fallback);
    };
  }, [color]);

  useEffect(() => {
    setShadowBugs(null);
  }, [projectId]);

  const dataBugs = shadowBugs || bugs;

  const lastVersionRaw = versions[versions.length - 1] || null;

  const lastVersion = useMemo(
    () => applyMetaToVersion(projectId, lastVersionRaw),
    [projectId, lastVersionRaw, versionTick]
  );

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const countdownInfo = useMemo(() => {
    if (!lastVersion?.deadline) return null;
    const ms = new Date(lastVersion.deadline) - now;
    if (ms <= 0) return { text: "Süre doldu!", pct: 100, critical: true };

    const created = lastVersion?.created_at ? new Date(lastVersion.created_at) : null;
    const deadline = new Date(lastVersion.deadline);

    const total = created ? deadline - created : 0;
    const pct = total > 0 ? Math.min(100, ((total - ms) / total) * 100) : 0;

    let level = "ok";
    if (pct > 66) level = "warn";
    if (ms < 86400000) level = "danger";
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return { text: `${d}g ${h}s ${m}d`, pct, level };
  }, [lastVersion, now]);

  const handleFileUpload = async () => {
    if (!form.file) return "";
    setForm((f) => ({ ...f, uploadStatus: "Yükleniyor..." }));
    try {
      const url = await uploadFile(form.file);
      setForm((f) => ({
        ...f,
        fileUrl: url,
        uploadStatus: "Yükleme başarılı!"
      }));
      return url;
    } catch {
      setForm((f) => ({ ...f, uploadStatus: "Yükleme başarısız!" }));
      return "";
    }
  };

  const ensureFileUrl = useCallback(async () => {
    const already = (form.fileUrl || "").trim();
    if (already) return already;
    if (!form.file) return "";
    const url = await handleFileUpload();
    return (url || "").trim();
  }, [form.file, form.fileUrl]);

  const groupByVersion = useCallback((list) => {
    const map = {};
    (list || []).forEach((b) => {
      const key = (b.version_name || "v1.0").toString();
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    return map;
  }, []);

  const refreshShadow = useCallback(async () => {
    const freshList = await getBugsByProject(projectId);
    setShadowBugs(groupByVersion(freshList));
  }, [projectId, groupByVersion]);

  const copyText = useCallback(async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      showToast({
        title: "Kopyalandı",
        description: "Panoya kopyalandı.",
        variant: "success",
        position: "bottom-right"
      });
    } catch {
      showToast({
        title: "Kopyalama başarısız",
        description: "Elle kopyalamanız gerekebilir.",
        variant: "error",
        position: "bottom-right"
      });
    }
  }, []);

  const downloadFile = useCallback(async (url, preferredName) => {
    const u = (url || "").trim();
    if (!u) return;

    const name = (preferredName || getFileName(u) || "dosya").toString();

    try {
      const res = await fetch(u, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        cache: "no-store"
      });

      if (!res.ok) throw new Error("download_failed");

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(blobUrl);

      showToast({
        title: "İndiriliyor",
        description: "Dosya indirilmeye başladı.",
        variant: "success",
        position: "bottom-right"
      });
    } catch {
      try {
        const a = document.createElement("a");
        a.href = u;
        a.download = name;
        a.target = "_blank";
        a.rel = "noreferrer";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        showToast({
          title: "İndirme başlatıldı",
          description: "Tarayıcı dosyayı yeni sekmede indirebilir.",
          variant: "success",
          position: "bottom-right"
        });
      } catch {
        showToast({
          title: "İndirme başarısız",
          description: "Bağlantı indirilemedi.",
          variant: "error",
          position: "bottom-right"
        });
      }
    }
  }, []);

  const openAttachmentInModal = useCallback(
    (url, fallbackTitle, kindOverride) => {
      const u = (url || "").trim();
      if (!u) return;

      const kind =
        kindOverride ||
        (isImageUrl(u) ? "image" : isPdfUrl(u) ? "pdf" : "file");

      const title = (fallbackTitle || getFileName(u) || getDisplaySource(u) || "Ek").toString();

      setAttViewer({
        open: true,
        url: u,
        title,
        kind
      });
    },
    []
  );

  const handleInviteUser = async (email) => {
    const value = String(email ?? form.email ?? "").trim();

    if (!value) {
      showToast({
        title: "Uyarı",
        description: "E-posta boş olamaz",
        variant: "error",
        position: "bottom-right"
      });
      return;
    }

    const copyToClipboard = async (text) => {
      const t = String(text || "").trim();
      if (!t) return;

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(t);
        } else {
          const ta = document.createElement("textarea");
          ta.value = t;
          ta.setAttribute("readonly", "");
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
      } catch {
      }
    };

    try {
      let res = null;

      try {
        if (typeof addUser === "function") {
          res = await addUser(value);
        }
      } catch {
        res = null;
      }

      if (!res) {
        res = await createInvite(projectId, value);
      }

      if (res?.status !== "success") {
        throw new Error(res?.message || "İşlem başarısız!");
      }

      const inviteUrl = String(res?.invite_url || "").trim();
      const addedUserEmail = String(res?.user?.email || "").trim();
      const addedUserName = String(res?.user?.name || "").trim();

      if (addedUserEmail) {
        showToast({
          title: "Kullanıcı eklendi",
          description: addedUserName ? `${addedUserName} (${addedUserEmail}) projeye eklendi.` : `${addedUserEmail} projeye eklendi.`,
          variant: "success",
          position: "bottom-right"
        });
      } else if (inviteUrl) {
        await copyToClipboard(inviteUrl);
        showToast({
          title: "Davet linki oluşturuldu",
          description: inviteUrl,
          variant: "success",
          position: "bottom-right"
        });
      } else {
        showToast({
          title: "Başarılı",
          description: res?.message || "İşlem tamamlandı.",
          variant: "success",
          position: "bottom-right"
        });
      }

      setForm((f) => ({ ...f, email: "" }));
      dispatch({ type: "user", open: false });
    } catch (e) {
      console.error("Kullanıcı ekleme/davet hatası:", e);
      showToast({
        title: "Hata",
        description: e?.message || "İşlem başarısız.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };

  const handleRemoveProjectUser = async (user) => {
    const targetId = String(user?.id ?? user?.user_id ?? "").trim();

    if (!targetId) {
      showToast({
        title: "Hata",
        description: "Çıkarılacak kullanıcı bulunamadı.",
        variant: "error",
        position: "bottom-right"
      });
      return;
    }

    try {
      await removeUser(targetId);
      showToast({
        title: "Kullanıcı çıkarıldı",
        description: `${user?.name || user?.email || "Kullanıcı"} projeden çıkarıldı.`,
        variant: "success",
        position: "bottom-right"
      });
    } catch (e) {
      showToast({
        title: "Hata",
        description: e?.message || "Kullanıcı projeden çıkarılamadı.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };

  const handleChangeProjectUserRole = async (user, role) => {
    const targetId = String(user?.id ?? user?.user_id ?? "").trim();

    if (!targetId) {
      showToast({
        title: "Hata",
        description: "Rolü değiştirilecek kullanıcı bulunamadı.",
        variant: "error",
        position: "bottom-right"
      });
      return;
    }

    try {
      await changeUserRole(targetId, role);
      showToast({
        title: "Rol güncellendi",
        description: `${user?.name || user?.email || "Kullanıcı"} artık ${role === "manager" ? "yönetici" : "üye"}.`,
        variant: "success",
        position: "bottom-right"
      });
    } catch (e) {
      showToast({
        title: "Hata",
        description: e?.message || "Kullanıcı rolü güncellenemedi.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };

  const projectUsers = useMemo(
    () =>
      users.map((user) => {
        const isCurrentUser = isCurrentProjectUser(user, currentUserId, currentUserEmail);

        if (isCurrentUser && canManageProjectUsers && !user.isOwner && !user.isManager) {
          return {
            ...user,
            role: "manager",
            isManager: true,
            isCurrentUser
          };
        }

        return {
          ...user,
          isCurrentUser
        };
      }),
    [users, currentUserId, currentUserEmail, canManageProjectUsers]
  );

  const handleAddBug = async () => {
    try {
      if (versions.length === 0) await createVersion("v1.0");

      const versionName = (form.version || lastVersion?.version_name || "v1.0").toString();
      const fileUrlFinal = await ensureFileUrl();

      const res = await createBug({
        title: form.title,
        description: form.description,
        file_url: fileUrlFinal,
        version_name: versionName,
        source: "web",
        external_url: (form.externalUrl || "").trim() || undefined
      });

      const createdId = res?.id || res?.bug_id || null;

      if (createdId) {
        const newBug = {
          id: createdId,
          title: form.title,
          description: form.description,
          file_url: fileUrlFinal || "",
          version_name: versionName,
          is_completed: false,
          created_at: new Date().toISOString(),
          email: "",
          external_url: (form.externalUrl || "").trim() || ""
        };

        setShadowBugs((prev) => {
          const base = prev ? { ...prev } : groupByVersion(Object.values(bugs).flat());
          const key = versionName.toString();
          base[key] = base[key] ? [...base[key], newBug] : [newBug];
          return base;
        });
      }

      await refreshShadow();

      setForm({ ...initialForm, version: versionName });

      dispatch({ type: "bug", open: false });

      const myUserId = String(currentUserId || readMyUserId() || "").trim();
      const myUserIdInt = myUserId ? Number(myUserId) : 0;
      const hrefWithBug = withBugParam(window.location.pathname || "/", createdId);

      if (createdId) {
        await notifyProjectUsers({
          project_id: Number(projectId),
          actor_user_id: myUserIdInt,
          title: form.title ? `Yeni İş: ${form.title}` : "Yeni iş",
          project_name: projectName || "",
          href: hrefWithBug,
          bug_id: createdId
        });
      }

    } catch (e) {
      console.error("Yeni iş eklenemedi:", e);
      showToast({
        title: "Hata",
        description: "Yeni iş eklenemedi.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };


  const startEditBug = (bug) => {
    setIsEdit(true);
    setEditingBugId(bug.id);
    setForm((f) => ({
      ...f,
      id: bug.id,
      title: bug.title || "",
      description: bug.description || "",
      file: null,
      fileUrl: bug.file_url || "",
      version: bug.version_name || (lastVersion?.version_name || "v1.0"),
      externalUrl: bug.external_url || ""
    }));
    dispatch({ type: "bug", open: true });
  };

  const handleUpdateBug = async () => {
    try {
      if (!editingBugId) {
        showToast({
          title: "Hata",
          description: "Güncellenecek iş bulunamadı.",
          variant: "error",
          position: "bottom-right"
        });
        return;
      }
      if (!form.title?.trim()) {
        showToast({
          title: "Uyarı",
          description: "Başlık boş olamaz.",
          variant: "error",
          position: "bottom-right"
        });
        return;
      }

      const versionName = (form.version || lastVersion?.version_name || "v1.0").toString().trim();

      const versionObj = versions.find(
        (v) => (v.version_name || "").toString().trim() === versionName
      );

      const fileUrlFinal = await ensureFileUrl();

      const payload = {
        title: form.title.trim(),
        description: form.description || "",
        file_url: fileUrlFinal || "",
        version_name: versionName,
        project_id: projectId,
        uniq_id: projectId,
        uniq: projectId,
        external_url: (form.externalUrl || "").trim() || undefined,
        ...(versionObj?.id ? { version_id: versionObj.id } : {})
      };

      await updateBug(editingBugId, payload);

      await refreshShadow();

      setIsEdit(false);
      setEditingBugId(null);
      setForm({ ...initialForm, version: versionName });
      dispatch({ type: "bug", open: false });
    } catch (err) {
      console.error("Bug güncellenemedi:", err);
      showToast({
        title: "Hata",
        description: err?.message || "Güncelleme başarısız.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };

  const toggleAndRefresh = async (id, isCompleted) => {
    try {
      await toggleBug(id, isCompleted);
      await refreshShadow();
    } catch (e) {
      console.error("Durum değiştirilemedi:", e);
      showToast({
        title: "Hata",
        description: "Durum değiştirilemedi.",
        variant: "error",
        position: "bottom-right"
      });
    }
  };

  const removeAndRefresh = async (id, titleText) => {
    try {
      await removeBug(id);
      await refreshShadow();
      showToast({
        title: "İş silindi",
        description: `“${(titleText || "İş").toString()}” başarıyla silindi.`,
        variant: "success",
        position: "bottom-right"
      });
    } catch (e) {
      console.error("Silinemedi:", e);
      showToast({
        title: "Hata",
        description: `“${(titleText || "İş").toString()}” silinemedi.`,
        variant: "error",
        position: "bottom-right"
      });
    }
  };

  const buildPreview = useCallback(
    (v) =>
      `[${projectName} ${v}]\n----------------------------------\n\n${(dataBugs[v] || [])
        .map(
          (b) =>
            `${b.is_completed ? "✓" : "□"} ${b.title}\n    ${htmlToText(b.description || "")}`
        )
        .join("\n")}`,
    [dataBugs, projectName]
  );

  const openShare = (v) => {
    setForm((f) => ({ ...f, preview: buildPreview(v), nowVersion: v }));
    dispatch({ type: "share", open: true });
  };

  const handleShareInvite = useCallback(async () => {
    const link = `https://www.buglog.app/invite/${projectId}`;
    const text = `Sizi Buglog projeme davet ediyorum ${link}`;

    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ text });
        showToast({
          title: "Paylaşıldı",
          description: "Davet metni gönderildi.",
          variant: "success",
          position: "bottom-right"
        });
        return;
      } catch {
      }
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      showToast({
        title: "Bağlantı kopyalandı",
        description: "Davet metni panoya kopyalandı.",
        variant: "success",
        position: "bottom-right"
      });
    } catch {
      showToast({
        title: "Kopyalama başarısız",
        description: "Metni elle kopyalamanız gerekebilir.",
        variant: "error",
        position: "bottom-right"
      });
    }
  }, [projectId]);

  const openBugs = useMemo(
    () =>
      Object.values(dataBugs).reduce(
        (s, l) => s + l.filter((b) => !b.is_completed).length,
        0
      ),
    [dataBugs]
  );
  const completedBugs = useMemo(
    () =>
      Object.values(dataBugs).reduce(
        (s, l) => s + l.filter((b) => b.is_completed).length,
        0
      ),
    [dataBugs]
  );

  const versionList = (
    Object.keys(dataBugs).length
      ? Object.keys(dataBugs).map((n, i) => ({ id: i + 1, version_name: n }))
      : versions.length
        ? [...versions]
        : Object.keys(bugs).map((n) => ({ id: n, version_name: n }))
  ).sort((a, b) => {
    if (typeof a.id === "number" && typeof b.id === "number") {
      return a.id - b.id;
    }
    return String(a.version_name).localeCompare(String(b.version_name), undefined, {
      numeric: true,
      sensitivity: "base"
    });
  });

  const versionSig = useMemo(() => {
    try {
      return versionList
        .map((v) => `${String(v.id || "")}:${String(v.version_name || "")}`)
        .join("|");
    } catch {
      return String(Date.now());
    }
  }, [versionList]);

  useEffect(() => {
    const basePath = (() => {
      try {
        return (window.location.pathname || "").split("?")[0];
      } catch {
        return "";
      }
    })();

    const idx = readGlobalVersionIndex();
    let changed = false;

    const next = {
      byVersionKey: { ...(idx.byVersionKey || {}) },
      byId: { ...(idx.byId || {}) }
    };

    for (const ver of versionList) {
      const k = buildGlobalVersionKey(projectId, ver);
      if (!k) continue;

      const prevStoredRaw = next.byVersionKey[k];
      const prevStored = String(prevStoredRaw || "").trim();
      const prevDigits = getUidDigits(prevStored);
      const prevUid = prevDigits ? `${VERSION_UID_PREFIX}${prevDigits}` : "";

      const needNew = !isVersionUid(prevUid) || isLegacyPrefixedId(prevStored);

      let uid = prevUid;

      if (needNew) {
        uid = genVersionUid(next.byId);
        next.byVersionKey[k] = uid;
        changed = true;
      } else {
        if (prevStored !== uid) {
          next.byVersionKey[k] = uid;
          changed = true;
        }
      }

      const prevRec = next.byId[uid] && typeof next.byId[uid] === "object" ? next.byId[uid] : null;

      const rec = {
        id: uid,
        versionName: String(ver.version_name || ""),
        projectName: String(projectName || ""),
        path: String(basePath || ""),
        projectKey: String(k || "")
      };

      if (!prevRec) {
        next.byId[uid] = rec;
        changed = true;
      } else {
        const same =
          String(prevRec.versionName || "") === rec.versionName &&
          String(prevRec.projectName || "") === rec.projectName &&
          String(prevRec.path || "") === rec.path &&
          String(prevRec.projectKey || "") === rec.projectKey;

        if (!same) {
          next.byId[uid] = { ...prevRec, ...rec };
          changed = true;
        }
      }
    }

    if (changed) {
      writeGlobalVersionIndex(next);
      setVersionUidTick((x) => x + 1);
      try {
        window.dispatchEvent(new CustomEvent("versionUid:changed", { detail: { projectId } }));
      } catch {
      }
    }
  }, [projectId, projectName, versionSig]);

  const versionUidByName = useMemo(() => {
    const idx = readGlobalVersionIndex();
    const out = {};
    for (const ver of versionList) {
      const k = buildGlobalVersionKey(projectId, ver);
      if (!k) continue;

      const raw = idx.byVersionKey?.[k];
      const uid = normalizeVersionUid(raw);

      if (isVersionUid(uid)) out[String(ver.version_name || "")] = uid;
    }
    return out;
  }, [projectId, versionSig, versionUidTick]);

  useEffect(() => {
    try {
      const qs = new URLSearchParams(window.location.search || "");
      const vidRaw = qs.get("vid") || qs.get("id") || "";
      const uid = normalizeVersionUid(vidRaw);
      const vname = String(qs.get("ver") || "").trim();

      let targetUid = isVersionUid(uid) ? uid : "";

      if (!targetUid && vname) {
        const found = versionList.find(
          (v) => String(v.version_name || "").toLowerCase() === vname.toLowerCase()
        );
        if (found) {
          const idx = readGlobalVersionIndex();
          const k = buildGlobalVersionKey(projectId, found);
          const raw = idx.byVersionKey?.[k] || "";
          const nuid = normalizeVersionUid(raw);
          if (isVersionUid(nuid)) targetUid = nuid;
        }
      }

      if (targetUid) {
        const el = document.getElementById(`version-${targetUid}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    } catch {
    }
  }, [projectId, versionSig, versionUidTick]);

  const saveDeadline = async (iso) => {
    if (!lastVersion) return;
    patchVersionMeta(projectId, lastVersion, { deadline: iso ?? "" });
    setVersionTick((x) => x + 1);
    setDeadlineDlg({ open: false, id: null, date: "" });
  };

  const closeVersion = async () => {
    if (!lastVersion) return;
    patchVersionMeta(projectId, lastVersion, { closed_at: new Date().toISOString() });
    setVersionTick((x) => x + 1);
  };

  const reopenVersion = async () => {
    if (!lastVersion) return;
    patchVersionMeta(projectId, lastVersion, { closed_at: "" });
    setVersionTick((x) => x + 1);
  };

  const headerButtons = [
    token === "11771177"
      ? {
        icon: <FiLogIn />,
        label: "Giriş Yap",
        onClick: () => navigate("/login")
      }
      : {
        icon: <FiUserPlus />,
        label: "Kullanıcılar",
        onClick: () => dispatch({ type: "user", open: true })
      },
    {
      icon: <img src="/branch.svg" alt="Yeni Sürüm" height={15} />,
      label: "Yeni Sürüm",
      onClick: () => dispatch({ type: "version", open: true })
    },
    {
      icon: <FiMoreHorizontal />,
      onClick: () => dispatch({ type: "info", open: true })
    }
  ];

  const [expanded, setExpanded] = useState({});
  const toggleDetails = useCallback((id) => setExpanded((s) => ({ ...s, [id]: !s[id] })), []);

  const [attachmentsMap, setAttachmentsMap] = useState({});
  const fetchedIdsRef = useRef(new Set());
  const lastViewedIdRef = useRef(null);

  const fetchBugAttachments = useCallback(async (bugId) => {
    try {
      const data = await getBugDetails(bugId);
      const list = Array.isArray(data?.attachments) ? data.attachments : [];
      setAttachmentsMap((prev) => ({ ...prev, [bugId]: list }));
      fetchedIdsRef.current.add(bugId);
    } catch {
    }
  }, []);

  useEffect(() => {
    const all = Object.values(dataBugs || {}).flat();
    const ids = all.map((b) => b.id).filter(Boolean);
    const toFetch = ids.filter((i) => !fetchedIdsRef.current.has(i)).slice(0, 40);

    let cancelled = false;
    (async () => {
      for (const bid of toFetch) {
        if (cancelled) return;
        await fetchBugAttachments(bid);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dataBugs, fetchBugAttachments]);

  useEffect(() => {
    if (detailId) {
      lastViewedIdRef.current = detailId;
    } else if (lastViewedIdRef.current) {
      fetchBugAttachments(lastViewedIdRef.current);
    }
  }, [detailId, fetchBugAttachments]);

  const renderAttachmentPreview = useMemo(() => {
    if (!attViewer?.open || !attViewer?.url) return null;

    const u = attViewer.url;
    const kind = attViewer.kind;

    if (kind === "image") {
      return (
        <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <img
            src={u}
            alt={attViewer.title || "Ek"}
            style={{ maxWidth: "100%", maxHeight: 520, borderRadius: 12 }}
          />
        </div>
      );
    }

    if (kind === "pdf") {
      return (
        <div style={{ width: "100%", height: 560, borderRadius: 12, overflow: "hidden" }}>
          <iframe
            title={attViewer.title || "PDF"}
            src={u}
            style={{ width: "100%", height: "100%", border: "0" }}
          />
        </div>
      );
    }

    if (kind === "external") {
      return (
        <div style={{ width: "100%", height: 560, borderRadius: 12, overflow: "hidden" }}>
          <iframe
            title={attViewer.title || "Bağlantı"}
            src={u}
            style={{ width: "100%", height: "100%", border: "0" }}
          />
        </div>
      );
    }

    return (
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <FiPaperclip />
          <div style={{ display: "grid", gap: 2 }}>
            <div style={{ fontWeight: 600 }}>{attViewer.title || getFileName(u)}</div>
            <div style={{ opacity: 0.7, fontSize: 13 }}>
              {isFirebaseStorageUrl(u) ? getFirebaseObjectPath(u) || getFileName(u) : getDisplaySource(u)}
            </div>
          </div>
        </div>

        <div className="modal-buttons" style={{ justifyContent: "flex-start" }}>
          <button
            type="button"
            className="modal-secondary-button"
            onClick={() => copyText(u)}
          >
            Bağlantıyı Kopyala
          </button>
          <button
            type="button"
            className="modal-add-button"
            onClick={() => downloadFile(u, attViewer.title || getFileName(u))}
          >
            İndir
          </button>
        </div>
      </div>
    );
  }, [attViewer, copyText, downloadFile]);

  return (
    <div>
      <Header buttons={headerButtons} />
      <div className="layout">
        <button
          type="button"
          className="back-btn back-btn--outside"
          aria-label="Geri dön"
          title="Dashboard'a dön"
          onClick={() => navigate("/")}
        >
          <FiChevronLeft size={18} />
        </button>

        <aside className="sidebar">
          <div className="sidebar-header">
            <div
              className={`avatar ${cover ? "has-cover" : ""}`}
              style={
                cover
                  ? { backgroundImage: `url(${cover})` }
                  : color
                    ? { background: color, color: "#fff" }
                    : undefined
              }
            >
              {!cover && initials}
            </div>
            <div className="sidebar-title-wrap">
              <div className="sidebar-title-row">
                <h2 className="sidebar-title">{projectName}</h2>
              </div>

              <div className="sidebar-actions">
                <button
                  onClick={() => dispatch({ type: "info", open: true })}
                  className="sidebar-desc"
                >
                  Detay
                </button>
                <button
                  onClick={() => setEditModalOpen(true)}
                  className="sidebar-desc"
                  style={{ marginLeft: 8 }}
                >
                  Düzenle
                </button>
              </div>
            </div>
          </div>

          <section className="project-panel">
            <div className="project-panel-head">
              <div>
                <h3>Projeler</h3>
                <p>Projeyi buradan değiştir.</p>
              </div>
              <button
                type="button"
                className={`project-panel-toggle${projectPanelOpen ? " open" : ""}`}
                onClick={() => setProjectPanelOpen((prev) => !prev)}
                aria-expanded={projectPanelOpen}
              >
                <span>{projectPanelOpen ? "Listeyi Gizle" : "Listeyi Aç"}</span>
                <FiChevronDown size={16} />
              </button>
            </div>

            <div className="project-panel-current">
              <span>Aktif Proje</span>
              <strong>{projectName}</strong>
            </div>

            {projectPanelOpen && (
              <>
                <label className="project-panel-sort" htmlFor="sidebar-project-sort">
                  <span>Sırala</span>
                  <select
                    id="sidebar-project-sort"
                    value={projectSort}
                    onChange={(e) => setProjectSort(e.target.value)}
                  >
                    <option value="az">A-Z</option>
                    <option value="za">Z-A</option>
                  </select>
                </label>

                <div className="project-panel-list">
                  {projectOptionsLoading ? (
                    <div className="project-panel-empty">Projeler yükleniyor...</div>
                  ) : sortedProjectOptions.length === 0 ? (
                    <div className="project-panel-empty">Görüntülenecek proje bulunamadı.</div>
                  ) : (
                    sortedProjectOptions.map((project) => {
                      const itemId = String(project?.uniq || project?.id || "").trim();
                      const itemName = getProjectDisplayName(project) || "Proje";
                      const isCurrent = itemId === String(projectId || "").trim();

                      return (
                        <button
                          key={itemId}
                          type="button"
                          className={`project-panel-item${isCurrent ? " active" : ""}`}
                          onClick={() => !isCurrent && handleProjectSwitch(project)}
                          disabled={isCurrent}
                        >
                          <div className="project-panel-copy">
                            <strong>{itemName}</strong>
                            <span>{isCurrent ? "Şu an açık proje" : "Geçiş yapmak için tıkla"}</span>
                          </div>
                          <span className="project-panel-state">
                            {isCurrent ? "Aktif" : "Aç"}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </section>

          {lastVersion && (
            <div className="version-box">
              <div className="version-box-title">
                {lastVersion.version_name}
                {lastVersion.closed_at ? <FiCheck /> : <FiAlertTriangle color="#FFCC00" />}
              </div>

              {lastVersion.closed_at ? (
                <>
                  <p className="deadline">
                    <FiCheck size={13} style={{ marginRight: 4 }} />
                    {new Date(lastVersion.closed_at).toLocaleDateString("tr-TR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric"
                    })}{" "}
                    · Kapatıldı
                  </p>

                  {lastVersion.deadline ? (
                    <p className="deadline" style={{ marginTop: 0 }}>
                      <FiClock size={13} style={{ marginRight: 4 }} />
                      {new Date(lastVersion.deadline).toLocaleDateString("tr-TR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric"
                      })}{" "}
                      · Yayın tarihi
                    </p>
                  ) : null}

                  <button
                    className="close-version-btn secondary"
                    onClick={() => setReopenOpen(true)}
                  >
                    Sürümü Yeniden Aç
                  </button>
                </>
              ) : (
                <>
                  {lastVersion.deadline ? (
                    <>
                      <p className="deadline">
                        <FiClock size={13} style={{ marginRight: 4 }} />
                        {new Date(lastVersion.deadline).toLocaleDateString("tr-TR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric"
                        })}{" "}
                        ·{" "}
                        <span className={`count ${countdownInfo?.level}`}>{countdownInfo?.text}</span>
                      </p>
                      <div className="bar">
                        <div style={{ width: `${countdownInfo?.pct || 0}%` }} />
                      </div>
                      <div className="btn-row">
                        <button
                          className="close-version-btn secondary"
                          onClick={() =>
                            setDeadlineDlg({
                              open: true,
                              id: lastVersion.id,
                              date: lastVersion.deadline
                            })
                          }
                        >
                          Düzenle
                        </button>
                        <button
                          className="close-version-btn secondary danger"
                          onClick={() => saveDeadline(null)}
                        >
                          <FiX size={14} /> Sıfırla
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      className="close-version-btn secondary"
                      onClick={() => setDeadlineDlg({ open: true, id: lastVersion.id, date: "" })}
                    >
                      Yayın Tarihi Ayarla
                    </button>
                  )}

                  <button className="close-version-btn" onClick={() => setCompleteOpen(true)}>
                    Sürümü Tamamla
                  </button>
                </>
              )}
            </div>
          )}

          <ul className="sidebar-stats">
            <li className="stat-item">
              <span className="stat-number">{versionList.length}</span>
              <span className="stat-label">Sürüm</span>
            </li>
            <li className="stat-item">
              <span className="stat-number">{openBugs + completedBugs}</span>
              <span className="stat-label">İş</span>
            </li>
            <li className="stat-item">
              <span className="stat-number">{completedBugs}</span>
              <span className="stat-label">Tamamlandı</span>
            </li>
            <li className="stat-item">
              <span className="stat-number">{openBugs}</span>
              <span className="stat-label">Açık</span>
            </li>
          </ul>

          <h3 className="sidebar-sub">
            Kullanıcılar
            <button onClick={() => dispatch({ type: "user", open: true })}>
              {canManageProjectUsers ? "Yönet" : "Görüntüle"}
            </button>
            {canManageProjectUsers ? (
              <button onClick={handleShareInvite}>Paylaş</button>
            ) : null}
          </h3>

          <ul className="sidebar-users">
            {projectUsers.map((u, index) => (
              <li
                key={u.id || u.email || `${u.name}-${index}`}
                className="sidebar-user-item"
              >
                <div className="sidebar-user-main">
                  <span className="dot" />
                  <div className="sidebar-user-copy">
                    <span className="sidebar-user-name">{u.name}</span>
                    {u.email ? (
                      <span className="sidebar-user-email">{u.email}</span>
                    ) : null}
                  </div>
                </div>

                <div className="sidebar-user-controls">
                  <div className="sidebar-user-badges">
                    <span className={`sidebar-user-role ${u.role || "member"}`}>
                      {getProjectUserRoleLabel(u.role)}
                    </span>
                    {u.isCurrentUser ? (
                      <span className="sidebar-user-self">Sen</span>
                    ) : null}
                  </div>

                  {canManageProjectUsers && !u.isOwner && !u.isCurrentUser ? (
                    <button
                      type="button"
                      className="sidebar-user-remove"
                      onClick={() => handleRemoveProjectUser(u)}
                    >
                      Çıkar
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
            {!projectUsers.length && <li style={{ opacity: 0.3 }}>Kullanıcı bulunamadı</li>}
          </ul>
        </aside>

        <div className="bugs-container">
          <header className="bugs-header">
            <button
              onClick={() => {
                setForm((prev) => ({
                  ...initialForm,
                  version: lastVersion?.version_name || prev.version || "v1.0"
                }));
                setIsEdit(false);
                setEditingBugId(null);
                dispatch({ type: "bug", open: true });
              }}
              className="add-bug-button"
            >
              <div className="add-bug-icon">
                <FiPlusCircle size={18} />
              </div>
              <span className="add-bug-text">Yeni İş Ekle</span>
            </button>
          </header>

          <div className="task-search">
            <FiSearch size={18} />
            <input
              type="text"
              placeholder="İşlerin içinde ara.."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="bug-list">
            {loading
              ? Array.from({ length: 10 }).map((_, i) => <ProjectCardLoader key={i} />)
              : versionList.map((ver) => {
                const verUid = versionUidByName[String(ver.version_name || "")] || "";
                return (
                  <div
                    key={ver.id}
                    className="version-group"
                    id={verUid ? `version-${verUid}` : undefined}
                  >
                    <div className="versionTitle">
                      <h2>{ver.version_name}</h2>

                      {verUid ? (
                        <button
                          type="button"
                          onClick={() => copyText(verUid)}
                          title="Sürüm ID kopyala"
                          className="version-uid"
                        >
                          {verUid}
                        </button>
                      ) : null}

                      <button
                        onClick={() =>
                          setRenameDlg({
                            open: true,
                            id: ver.id,
                            name: ver.version_name
                          })
                        }
                      >
                        <FiEdit2 size={16} color="#989898" />
                      </button>
                      <button onClick={() => openShare(ver.version_name)}>
                        <FiShare size={17} color="#489BF4" />
                      </button>
                    </div>

                    {[...(dataBugs[ver.version_name] || [])]
                      .filter(
                        (b) =>
                          b.title.toLowerCase().includes(search.toLowerCase()) ||
                          htmlToText(b.description || "").toLowerCase().includes(search.toLowerCase())
                      )
                      .sort((a, b) => Number(a.is_completed) - Number(b.is_completed))
                      .map((b) => {
                        const text = htmlToText(b.description || "");
                        const lines = text.split("\n");
                        const hasMoreThanTwo = lines.length > 2;
                        const preview = hasMoreThanTwo ? lines.slice(0, 2).join("\n") : text;
                        const isOpen = !!expanded[b.id];

                        const extraAtts = attachmentsMap[b.id] || [];
                        const showChips = extraAtts.length > 0;
                        const visibleChips = extraAtts.slice(0, 3);
                        const restCount = Math.max(0, extraAtts.length - visibleChips.length);

                        return (
                          <div key={b.id} className={`bug-card${b.is_completed ? " completed" : ""}`}>
                            <div
                              className="custom-checkbox"
                              onClick={() => toggleAndRefresh(b.id, b.is_completed)}
                            >
                              <input type="checkbox" checked={b.is_completed} readOnly />
                              <span className="checkmark" />
                            </div>

                            <div className="bug-info">
                              <div className="bug-info-left">
                                <h3 className="bug-title">{b.title}</h3>

                                {b.description && (
                                  <>
                                    {isOpen ? (
                                      <div
                                        className="bug-details expanded"
                                        dangerouslySetInnerHTML={{ __html: b.description }}
                                      />
                                    ) : (
                                      <p className="bug-details collapsed">{preview}</p>
                                    )}
                                    {hasMoreThanTwo && (
                                      <button
                                        type="button"
                                        className="bug-more"
                                        onClick={() => toggleDetails(b.id)}
                                      >
                                        {isOpen ? "Daha az" : "Daha fazla detay"}
                                      </button>
                                    )}
                                  </>
                                )}

                                {(b.file_url || b.external_url) && (
                                  <div className="bug-attachment">
                                    {b.file_url && (
                                      <>
                                        <a
                                          href={b.file_url}
                                          className="bug-attachment-link"
                                          title="Eki aç"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            openAttachmentInModal(b.file_url, `Ek: ${getFileName(b.file_url)}`);
                                          }}
                                        >
                                          <FiPaperclip />
                                          <span className="file-label">Ek: {getFileName(b.file_url)}</span>
                                        </a>

                                        {isImageUrl(b.file_url) && (
                                          <a
                                            href={b.file_url}
                                            className="bug-attachment-thumb"
                                            title="Önizlemeyi aç"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              openAttachmentInModal(b.file_url, getFileName(b.file_url), "image");
                                            }}
                                          >
                                            <img src={b.file_url} alt="Ek önizleme" />
                                          </a>
                                        )}
                                      </>
                                    )}

                                    {b.external_url && (
                                      <a
                                        href={b.external_url}
                                        className="bug-attachment-link"
                                        title={b.external_url}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          openAttachmentInModal(
                                            b.external_url,
                                            isFirebaseStorageUrl(b.external_url)
                                              ? `Bağlantı: ${getFirebaseObjectPath(b.external_url) || getFileName(b.external_url)}`
                                              : `Bağlantı: ${getHost(b.external_url)}`,
                                            "external"
                                          );
                                        }}
                                      >
                                        <FiLink />
                                        <span className="file-label">
                                          Bağlantı: {isFirebaseStorageUrl(b.external_url) ? (getFirebaseObjectPath(b.external_url) || getFileName(b.external_url)) : getHost(b.external_url)}
                                        </span>
                                      </a>
                                    )}
                                  </div>
                                )}

                                {showChips && (
                                  <div className="bug-attachment-list">
                                    {visibleChips.map((a) => (
                                      <a
                                        key={a.id}
                                        href={a.file_path}
                                        className="att-chip"
                                        title={a.file_path}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          openAttachmentInModal(a.file_path, getFileName(a.file_path));
                                        }}
                                      >
                                        <FiPaperclip />
                                        <span className="file-label">{getFileName(a.file_path)}</span>
                                      </a>
                                    ))}

                                    {restCount > 0 && (
                                      <button
                                        type="button"
                                        className="att-more"
                                        onClick={() => setDetailId(b.id)}
                                        title="Tüm ekleri gör"
                                      >
                                        +{restCount} daha
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className="bug-info-right">
                                <div>
                                  <button onClick={() => setDetailId(b.id)}>
                                    <span>Detay</span>
                                  </button>
                                  <button onClick={() => startEditBug(b)}>
                                    <span>Düzenle</span>
                                  </button>
                                  <button
                                    onClick={() =>
                                      setDeleteBugDlg({
                                        open: true,
                                        id: b.id,
                                        title: b.title || "İş"
                                      })
                                    }
                                    className="delete-button"
                                  >
                                    <FiTrash color="#B9B9B9" size={20} />
                                  </button>
                                </div>
                                <div className="bug-info-right-mail">{b.email}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <BugModal
        open={modal.bug}
        close={() => {
          setIsEdit(false);
          setEditingBugId(null);
          setForm((prev) => ({ ...initialForm, version: prev.version }));
          dispatch({ type: "bug", open: false });
        }}
        form={form}
        setForm={setForm}
        upload={handleFileUpload}
        add={handleAddBug}
        isEdit={isEdit}
        save={handleUpdateBug}
      />

      <VersionModal
        open={modal.version}
        close={() => dispatch({ type: "version", open: false })}
        value={form.version}
        setValue={(v) => setForm((f) => ({ ...f, version: v }))}
        create={createVersion}
      />

      <UserModal
        open={modal.user}
        close={() => dispatch({ type: "user", open: false })}
        email={form.email}
        setEmail={(e) => setForm((f) => ({ ...f, email: e }))}
        add={handleInviteUser}
        users={projectUsers}
        removeUser={handleRemoveProjectUser}
        changeUserRole={handleChangeProjectUserRole}
        canManageMembers={canManageProjectUsers}
        currentUserId={currentUserId}
        currentUserEmail={currentUserEmail}
      />

      <InfoModal
        open={modal.info}
        close={() => dispatch({ type: "info", open: false })}
        projectName={projectName}
        projectId={projectId}
        isOwner={isOwner === "1"}
        shareUrl={() => shareProjectInvite(projectName, projectId)}
        leave={leave}
        askDelete={() => dispatch({ type: "deleteConfirm", open: true })}
      />

      <DeleteModal
        open={modal.deleteConfirm}
        close={() => dispatch({ type: "deleteConfirm", open: false })}
        projectName={projectName}
        confirm={form.confirmName}
        setConfirm={(c) => setForm((f) => ({ ...f, confirmName: c }))}
        remove={async () => {
          await removeProject();
          navigate("/");
        }}
      />

      <ShareModal
        open={modal.share}
        close={() => dispatch({ type: "share", open: false })}
        preview={form.preview}
        projectName={projectName}
        version={form.nowVersion}
      />

      <RenameVersionModal
        open={renameDlg.open}
        current={renameDlg.name}
        close={() => setRenameDlg({ ...renameDlg, open: false })}
        save={(n) => renameVersion(renameDlg.id, n)}
      />

      <DeadlineModal
        open={deadlineDlg.open}
        currentDate={deadlineDlg.date}
        close={() => setDeadlineDlg({ ...deadlineDlg, open: false })}
        save={saveDeadline}
      />

      <Modal open={completeOpen} close={() => setCompleteOpen(false)}>
        <h2>Sürümü Tamamla</h2>
        <p className="modal-desc">
          “{lastVersion?.version_name}” sürümünü tamamlamak istediğinize emin misiniz?
        </p>
        <div className="modal-buttons">
          <button
            type="button"
            className="modal-secondary-button"
            onClick={() => setCompleteOpen(false)}
          >
            İptal
          </button>
          <button
            type="button"
            className="modal-add-button"
            onClick={async () => {
              await closeVersion();
              setCompleteOpen(false);
              showToast({
                title: "Sürüm tamamlandı",
                description: `“${lastVersion?.version_name}” kapatıldı.`,
                variant: "success",
                position: "bottom-right"
              });
              dispatch({ type: "version", open: true });
            }}
          >
            Evet, Tamamla
          </button>
        </div>
      </Modal>

      <Modal open={reopenOpen} close={() => setReopenOpen(false)}>
        <h2>Sürümü Yeniden Aç</h2>
        <p className="modal-desc">
          “{lastVersion?.version_name}” sürümünü yeniden açmak istediğinize emin misiniz?
        </p>
        <div className="modal-buttons">
          <button
            type="button"
            className="modal-secondary-button"
            onClick={() => setReopenOpen(false)}
          >
            İptal
          </button>
          <button
            type="button"
            className="modal-add-button"
            onClick={async () => {
              await reopenVersion();
              setReopenOpen(false);
              showToast({
                title: "Sürüm açıldı",
                description: `“${lastVersion?.version_name}” tekrar açıldı.`,
                variant: "success",
                position: "bottom-right"
              });
            }}
          >
            Evet, Aç
          </button>
        </div>
      </Modal>

      <Modal
        open={deleteBugDlg.open}
        close={() => setDeleteBugDlg({ open: false, id: null, title: "" })}
      >
        <div className="bug-delete-confirm">
          <h2>İşi Sil</h2>
          <p className="modal-desc">
            “{deleteBugDlg.title}” işini silmek istediğinize emin misiniz?
          </p>
          <div className="modal-buttons">
            <button
              type="button"
              className="modal-secondary-button"
              onClick={() => setDeleteBugDlg({ open: false, id: null, title: "" })}
            >
              İptal
            </button>
            <button
              type="button"
              className="modal-add-button"
              onClick={async () => {
                if (deleteBugDlg.id) {
                  await removeAndRefresh(deleteBugDlg.id, deleteBugDlg.title);
                }
                setDeleteBugDlg({ open: false, id: null, title: "" });
              }}
            >
              Tamam
            </button>
          </div>
        </div>
      </Modal>

      <EditProjectModal
        open={editModalOpen}
        close={() => setEditModalOpen(false)}
        projectId={projectId}
        projectName={projectName}
      />

      {detailId && <BugDetail bugId={detailId} onClose={() => setDetailId(null)} />}

      <Modal
        open={attViewer.open}
        close={() => setAttViewer({ open: false, url: "", title: "", kind: "file" })}
      >
        <div style={{ display: "grid", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ display: "grid", gap: 2 }}>
              <h2 style={{ margin: 0 }}>{attViewer.title || "Ek"}</h2>
              {attViewer.url ? (
                <div style={{ opacity: 0.7, fontSize: 13 }}>
                  {isFirebaseStorageUrl(attViewer.url)
                    ? (getFirebaseObjectPath(attViewer.url) || getFileName(attViewer.url))
                    : getDisplaySource(attViewer.url)}
                </div>
              ) : null}
            </div>

            {attViewer.url ? (
              <div className="modal-buttons" style={{ margin: 0 }}>
                <button
                  type="button"
                  className="modal-secondary-button"
                  onClick={() => copyText(attViewer.url)}
                >
                  Kopyala
                </button>
                <button
                  type="button"
                  className="modal-add-button"
                  onClick={() => downloadFile(attViewer.url, attViewer.title || getFileName(attViewer.url))}
                >
                  İndir
                </button>
              </div>
            ) : null}
          </div>

          {renderAttachmentPreview}
        </div>
      </Modal>
    </div>
  );
};

export default Bugs;
